// Left-rail drawer that shows fresh inbound runs from seat 2, ready
// for the dispatcher to drag onto the grid / timeline.
//
// Closed state: thin handle on the left edge with a count badge that
// pulses gold when there's still work to do.
//
// Open state: scrollable list of FlightQueueCards. Each card is one
// inbound flight; expand the card to see its child RunTiles. Each
// RunTile is draggable and carries the runId via the DND.RUN_FROM_QUEUE
// MIME type.

import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Collapse } from '@mui/material';
import ChevronLeftIcon  from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon   from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { C } from '../styles';
import { useRunQueue, DND } from './RunQueueContext';
import RunStopsPopover from './RunStopsPopover';

const DRAWER_W = 320;
const HANDLE_W = 22;

// Demo "now" — used for time-to-arrival urgency. Matches mockAssignments
// so the queue's urgency pulse aligns with the rest of the demo.
const DEMO_NOW_MIN = 12 * 60;

// Translate an inbound's ETA into an urgency level for the queue
// pulses. Critical = bags landing in the next 45 min (need a driver
// briefed and in motion now). Soon = 45–60 min — heads up, queue this.
function urgencyFor(etaMins) {
  if (etaMins == null) return null;
  const mins = etaMins - DEMO_NOW_MIN;
  if (mins <= 45) return 'critical';   // pulse red
  if (mins <= 60) return 'soon';       // pulse orange
  return null;
}
const URGENCY_COLOR = {
  critical: '#FF0000',
  soon:     '#F6530F',  // freight orange — matches canonical end-of-turn
};

export default function RunQueueDrawer() {
  const {
    drawerOpen, toggleDrawer, queueByInbound, queueCount,
    drawerSide, toggleDrawerSide,
  } = useRunQueue();

  // Worst urgency across all queued inbounds drives the closed-state
  // pulse so the dispatcher can spot a "do something now" queue from
  // across the room even with the drawer hidden.
  const worstUrgency = (() => {
    let worst = null;
    for (const group of queueByInbound) {
      const eta = group.runs[0]?.inboundEtaMins;
      const u = urgencyFor(eta);
      if (u === 'critical') return 'critical';
      if (u === 'soon' && worst !== 'critical') worst = 'soon';
    }
    return worst;
  })();
  const isRight = drawerSide === 'right';

  return (
    <Box sx={{
      position: 'relative',
      width: drawerOpen ? DRAWER_W : HANDLE_W,
      flexShrink: 0,
      transition: 'width 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
      // Border lives on the side that faces the rest of the layout —
      // right edge when docked left, left edge when docked right.
      [isRight ? 'borderLeft' : 'borderRight']: `1px solid ${C.borderStrong}`,
      backgroundColor: C.surfaceSolid,
      display: 'flex', flexDirection: 'column',
      minHeight: 0,
      overflow: 'hidden',
    }}>
      {drawerOpen
        ? <OpenContents
            queueByInbound={queueByInbound}
            queueCount={queueCount}
            onClose={toggleDrawer}
            isRight={isRight}
            onToggleSide={toggleDrawerSide}
          />
        : <ClosedHandle
            queueCount={queueCount}
            urgency={worstUrgency}
            isRight={isRight}
            onOpen={toggleDrawer}
          />}
    </Box>
  );
}

// ── Closed state — thin handle with warning glow ──────────────────────

function ClosedHandle({ queueCount, urgency, isRight, onOpen }) {
  const hasWork = queueCount > 0;
  // Pulse color tiers: critical (red) > soon (orange) > calm (gold).
  // Calm = work in queue but nothing within the next hour — no alarm.
  const pulseColor = urgency === 'critical' ? URGENCY_COLOR.critical
                   : urgency === 'soon'     ? URGENCY_COLOR.soon
                   : C.accent;
  // Inset glow lives on the edge that faces the layout (mirrored).
  const insetEdge = isRight ? 'inset 3px 0 0' : 'inset -3px 0 0';
  const ChevronToOpen = isRight ? ChevronLeftIcon : ChevronRightIcon;

  return (
    <Tooltip
      title={hasWork
        ? `${queueCount} run${queueCount === 1 ? '' : 's'} waiting to assign${
            urgency === 'critical' ? ' — bags landing in <45 min'
          : urgency === 'soon'     ? ' — next bank in 45–60 min'
          : ''}`
        : 'Open run queue'}
      placement={isRight ? 'left' : 'right'}
    >
      <Box
        onClick={onOpen}
        sx={{
          flex: 1,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: hasWork
            ? `${insetEdge} ${pulseColor}, 0 0 18px ${pulseColor}55`
            : 'none',
          animation: hasWork ? 'queuePulse 1.6s ease-in-out infinite' : 'none',
          '@keyframes queuePulse': {
            '0%, 100%': { boxShadow: `${insetEdge} ${pulseColor}, 0 0 12px ${pulseColor}55` },
            '50%':      { boxShadow: `${insetEdge} ${pulseColor}, 0 0 22px ${pulseColor}AA` },
          },
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04)' },
        }}
      >
        <ChevronToOpen sx={{ color: hasWork ? pulseColor : C.textMuted, fontSize: 18 }} />
        {hasWork && (
          <Box sx={{
            mt: 1,
            backgroundColor: pulseColor,
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 'bold',
            borderRadius: '999px',
            minWidth: 18, height: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            px: 0.5,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {queueCount}
          </Box>
        )}
        <Typography sx={{
          mt: 1, color: C.textMuted, fontSize: '0.65rem', fontWeight: 'bold',
          writingMode: 'vertical-rl', transform: 'rotate(180deg)',
          letterSpacing: 0.5,
        }}>
          {hasWork ? 'QUEUE' : 'Queue'}
        </Typography>
      </Box>
    </Tooltip>
  );
}

// ── Open state ────────────────────────────────────────────────────────

function OpenContents({ queueByInbound, queueCount, onClose, isRight, onToggleSide }) {
  // Close chevron points toward the layout edge the drawer's docked on.
  const ChevronToClose = isRight ? ChevronRightIcon : ChevronLeftIcon;
  return (
    <>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center',
        px: 1.5, py: 1.25,
        borderBottom: `1px solid ${C.border}`,
        backgroundColor: C.surfaceHiSolid,
        flexShrink: 0,
      }}>
        <InboxOutlinedIcon sx={{ color: C.accent, fontSize: 18, mr: 1 }} />
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 'bold', color: C.textPrimary }}>
          From Seat 2
        </Typography>
        <Box sx={{
          ml: 1,
          backgroundColor: queueCount > 0 ? C.accent : C.surfaceAlt,
          color: queueCount > 0 ? '#000' : C.textMuted,
          fontSize: '0.7rem', fontWeight: 'bold',
          borderRadius: '999px',
          minWidth: 22, height: 18,
          px: 0.75,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {queueCount}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={`Dock drawer ${isRight ? 'left' : 'right'}`}>
          <IconButton size="small" onClick={onToggleSide} sx={{ color: C.textSecondary, mr: 0.5 }}>
            <SwapHorizIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Hide queue">
          <IconButton size="small" onClick={onClose} sx={{ color: C.textSecondary }}>
            <ChevronToClose fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* List */}
      <Box sx={{
        flex: 1, overflow: 'auto', minHeight: 0,
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(255, 255, 255, 0.18)', borderRadius: 3,
        },
      }}>
        {queueCount === 0 ? (
          <EmptyState />
        ) : (
          <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {queueByInbound.map(group => (
              <FlightQueueCard key={group.inboundId} group={group} />
            ))}
          </Box>
        )}
      </Box>
    </>
  );
}

function EmptyState() {
  return (
    <Box sx={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      px: 3, py: 6, textAlign: 'center',
    }}>
      <Typography sx={{ fontSize: '0.9rem', color: C.textSecondary, fontWeight: 'bold', mb: 0.5 }}>
        Queue's empty
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', color: C.textMuted }}>
        Seat 2 hasn't sent over any new runs.
      </Typography>
    </Box>
  );
}

// ── Per-flight card ───────────────────────────────────────────────────

function FlightQueueCard({ group }) {
  const [expanded, setExpanded] = useState(true);
  const sample = group.runs[0];
  const num = (sample.inboundFlightNumber || '').replace(/^WN\s*/, '');
  const totalBags = group.runs.reduce((t, r) => t + r.totalBags, 0);
  const hasHot      = group.runs.some(r => r.hasHot);
  const hasPriority = group.runs.some(r => r.hasPriority);
  // Time-to-arrival urgency. Drives a pulsing colored border on the
  // whole card so a glance at the drawer tells the dispatcher which
  // inbounds are running out of clock.
  const urgency = urgencyFor(sample.inboundEtaMins);
  const urgencyColor = urgency ? URGENCY_COLOR[urgency] : null;
  const minsUntil = Math.max(0, sample.inboundEtaMins - DEMO_NOW_MIN);

  return (
    <Box sx={{
      backgroundColor: 'rgba(17, 25, 36, 0.55)',
      border: `1px solid ${urgencyColor || C.border}`,
      borderRadius: 1,
      overflow: 'hidden',
      boxShadow: 'inset 0 0 12px rgba(17, 25, 36, 0.15), 0 4px 8px rgba(0, 0, 0, 0.25)',
      // Urgency pulse — red for ≤45 min, orange for 45–60 min.
      ...(urgencyColor && {
        animation: 'cardUrgencyPulse 1.6s ease-in-out infinite',
        '@keyframes cardUrgencyPulse': {
          '0%, 100%': { boxShadow: `inset 0 0 12px rgba(17, 25, 36, 0.15), 0 0 0 1px ${urgencyColor}55, 0 4px 8px rgba(0, 0, 0, 0.25)` },
          '50%':      { boxShadow: `inset 0 0 12px rgba(17, 25, 36, 0.15), 0 0 0 2px ${urgencyColor}, 0 4px 14px ${urgencyColor}66` },
        },
      }),
    }}>
      {/* Header — click to toggle expand */}
      <Box
        onClick={() => setExpanded(e => !e)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75,
          px: 1.25, py: 1,
          cursor: 'pointer',
          backgroundColor: hasHot      ? 'rgba(255, 0, 0, 0.08)'
                          : hasPriority ? 'rgba(249, 182, 18, 0.08)'
                          : 'transparent',
          borderBottom: expanded ? `1px solid ${C.divider}` : 'none',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04)' },
        }}
      >
        <ExpandMoreIcon sx={{
          color: C.textMuted, fontSize: 18,
          transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
          transition: 'transform 150ms',
        }} />
        <Typography sx={{
          fontSize: '0.95rem', fontWeight: 'bold', color: C.textPrimary,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {num}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: C.textSecondary }}>
          {sample.inboundFrom}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{
          fontSize: '0.75rem', color: C.textMuted, fontWeight: 'bold',
        }}>
          {sample.inboundGate}
        </Typography>
        <Typography sx={{
          fontSize: '0.8rem',
          color: urgencyColor || C.textPrimary,
          fontWeight: 'bold',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {sample.inboundEta}
        </Typography>
        {urgency && (
          <Typography sx={{
            fontSize: '0.66rem',
            color: urgencyColor,
            fontWeight: 'bold',
            letterSpacing: '0.04em',
            fontVariantNumeric: 'tabular-nums',
          }}>
            in {minsUntil}m
          </Typography>
        )}
      </Box>

      {/* Sub-header — runs / bags summary */}
      {expanded && (
        <Box sx={{
          px: 1.25, py: 0.5,
          display: 'flex', alignItems: 'center', gap: 1.5,
          borderBottom: `1px solid ${C.divider}`,
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
        }}>
          <Typography sx={{ fontSize: '0.7rem', color: C.textMuted }}>
            <Box component="span" sx={{ color: C.textPrimary, fontWeight: 'bold' }}>
              {group.runs.length}
            </Box>{' '}
            run{group.runs.length === 1 ? '' : 's'}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: C.textMuted }}>
            <Box component="span" sx={{ color: C.textPrimary, fontWeight: 'bold' }}>
              {totalBags}
            </Box>{' '}
            bags
          </Typography>
          {hasHot && <CategoryDot color={C.hot} label="HOT" />}
          {hasPriority && <CategoryDot color={C.warn} label="PRI" />}
        </Box>
      )}

      <Collapse in={expanded} timeout={150}>
        <Box sx={{ p: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {group.runs.map(run => <RunTile key={run.id} run={run} />)}
        </Box>
      </Collapse>
    </Box>
  );
}

function CategoryDot({ color, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: color }} />
      <Typography sx={{ fontSize: '0.65rem', color, fontWeight: 'bold' }}>{label}</Typography>
    </Box>
  );
}

// ── Per-run tile (draggable) ──────────────────────────────────────────

function RunTile({ run }) {
  const [stopsAnchor, setStopsAnchor] = useState(null);
  const { feasibility, startDragPreview, endDragPreview } = useRunQueue();
  const feasLevel = feasibility?.perRunLevel.get(run.id) || null;
  const feasCounts = feasibility?.perRunCount.get(run.id) || null;

  // Color the role chip based on FWD/AFT/Solo.
  let roleBg, roleFg;
  if (run.splitType === 'FWD')      { roleBg = C.fwdBg;     roleFg = C.fwd; }
  else if (run.splitType === 'AFT') { roleBg = C.aftBg;     roleFg = C.aft; }
  else if (run.slotLabel === 'Solo'){ roleBg = C.surfaceAlt; roleFg = C.textSecondary; }
  else                              { roleBg = C.accentBg;  roleFg = C.accent; }

  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(DND.RUN_FROM_QUEUE, run.id);
    e.dataTransfer.setData('text/plain', run.id);
    // Tell the grid which run is being dragged so it can highlight
    // candidate drivers' next empty slot.
    startDragPreview(run.id);
  };
  const handleDragEnd = () => {
    endDragPreview();
  };

  // Feasibility takes precedence over the run's connection category for
  // the left border — "can we crew this?" is the more actionable signal.
  // Falls through to the existing category color when feasibility is OK.
  const feasColor = feasLevel === 'red' ? C.hot
                  : feasLevel === 'gold' ? C.warn
                  : null;
  const categoryColor = run.isFreightOnly ? C.freight
                       : run.hasHot      ? C.hot
                       : run.hasPriority ? C.warn
                       : run.hasCold     ? C.cold
                       : C.borderStrong;
  const borderColor = feasColor || categoryColor;

  return (
    <Box
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.75,
        px: 1, py: 0.75,
        backgroundColor: 'rgba(30, 30, 30, 0.6)',
        border: `1px solid ${C.border}`,
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: 0.75,
        cursor: 'grab',
        transition: 'background-color 100ms, transform 100ms, box-shadow 100ms',
        // Subtle outer glow on infeasible runs so they read at a glance.
        ...(feasLevel === 'red' && {
          boxShadow: `0 0 0 1px ${C.hot}55, 0 0 10px rgba(255, 0, 0, 0.25)`,
        }),
        '&:hover': {
          backgroundColor: 'rgba(40, 40, 40, 0.75)',
        },
        '&:active': {
          cursor: 'grabbing',
          transform: 'scale(0.98)',
        },
      }}
    >
      {/* Role chip */}
      <Box sx={{
        backgroundColor: roleBg,
        color: roleFg,
        fontSize: '0.7rem', fontWeight: 'bold',
        px: 0.75, py: 0.2,
        borderRadius: 0.5,
        minWidth: 38, textAlign: 'center',
        flexShrink: 0,
      }}>
        {run.slotLabel}
      </Box>

      {/* Run details */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.78rem', color: C.textPrimary, fontWeight: 'bold',
          lineHeight: 1.15,
        }}>
          {run.isFreightOnly
            ? <Box component="span" sx={{ color: C.freight }}>Freight only</Box>
            : <>{run.totalBags} bags · {run.totalStops} stops</>}
        </Typography>
        <Typography sx={{ fontSize: '0.66rem', color: C.textMuted, lineHeight: 1.15 }}>
          tightest {run.tightestConnection}m
          {feasCounts && (() => {
            // Show green-gap drivers when ≥1 (comfortable assignment
            // pool). When 0 green, show how many yellow-gap drivers
            // could still take it. If 0 of both: "no crew".
            const { green, yellow } = feasCounts;
            if (green > 0) {
              return (
                <Box component="span" sx={{ ml: 0.75, color: C.textMuted }}>
                  · {green} crew
                </Box>
              );
            }
            if (yellow > 0) {
              return (
                <Box component="span" sx={{ ml: 0.75, color: C.warn, fontWeight: 'bold' }}>
                  · {yellow} tight
                </Box>
              );
            }
            return (
              <Box component="span" sx={{ ml: 0.75, color: C.hot, fontWeight: 'bold' }}>
                · no crew
              </Box>
            );
          })()}
        </Typography>
      </Box>

      {/* Peek-at-stops button */}
      <Tooltip title="View stops">
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setStopsAnchor(e.currentTarget); }}
          sx={{
            color: C.textMuted, p: 0.25,
            '&:hover': { color: C.accent, backgroundColor: 'rgba(249, 182, 18, 0.1)' },
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <RunStopsPopover
        anchorEl={stopsAnchor}
        run={run}
        onClose={() => setStopsAnchor(null)}
      />
    </Box>
  );
}
